import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  type FileTransferSliceState,
  FileTransferStateType,
  type TransferredFile,
  type DownloadableFile,
} from './fileTransfer.type';
import { getLogger } from '../../libs/logger';

const logger = getLogger('fileTransferSlice');

const initialState: FileTransferSliceState = {
  currentState: FileTransferStateType.NotStarted,
  filesAvailable: {},
  filesUploaded: [],
  requestMemberIds: [],
  fileTransferSessionId: undefined,
  uploadingFilesCount: 0,
};

export const fileTransferSlice = createSlice({
  name: 'fileTransfer',
  initialState: () => initialState,
  reducers: {
    resetFileTransferState: () => initialState,
    setFileTransferState: (
      state,
      action: PayloadAction<FileTransferStateType>,
    ) => {
      // Log sanitized data - exclude sensitive fields entirely
      const sanitizedToState = {
        currentState: state.currentState,
        requestMemberIds: state.requestMemberIds,
        uploadingFilesCount: state.uploadingFilesCount,
        // Exclude filesAvailable and filesUploaded for security
      };

      logger.log(`[fileTransfer/onTransition]`, {
        from: state.currentState,
        to: sanitizedToState,
        type: action.payload,
        sessionId: state.fileTransferSessionId,
      });
      state.currentState = action.payload;
    },
    setRequestMemberIds: (
      state,
      action: PayloadAction<FileTransferSliceState['requestMemberIds']>,
    ) => {
      state.requestMemberIds = action.payload;
    },

    setFileTransferSessionId: (
      state,
      action: PayloadAction<FileTransferSliceState['fileTransferSessionId']>,
    ) => {
      state.fileTransferSessionId = action.payload;
    },
    setFileAsDownloaded: (state, action: PayloadAction<string>) => {
      if (state.filesAvailable[action.payload] !== undefined) {
        (
          state.filesAvailable[action.payload] as DownloadableFile
        ).isDownloaded = true;
      } else {
        logger.error(
          `[fileTransfer/setFileAsDownloaded] Avaiable file not found: ${action.payload}`,
        );
      }
    },
    addFileUploaded: (state, action: PayloadAction<TransferredFile>) => {
      state.filesUploaded = [...state.filesUploaded, action.payload];
    },
    setFilesAvailable: (
      state,
      action: PayloadAction<FileTransferSliceState['filesAvailable']>,
    ) => {
      state.filesAvailable = action.payload;
    },
    setUploadingFilesCount: (
      state,
      action: PayloadAction<FileTransferSliceState['uploadingFilesCount']>,
    ) => {
      state.uploadingFilesCount = action.payload;
    },
  },
});

export const {
  setFileTransferState,
  setRequestMemberIds,
  setFileTransferSessionId,
  resetFileTransferState,
  addFileUploaded,
  setFilesAvailable,
  setFileAsDownloaded,
  setUploadingFilesCount,
} = fileTransferSlice.actions;
